import { ArrowRight, Twitter, Linkedin, Youtube, Facebook } from "lucide-react";
import logo from "@/assets/inext-logo.svg";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";

const columnGroups = [
  [
    {
      headingKey: "footerProducts",
      headingHref: "/products",
      links: [
        { labelKey: "footerLinkTv6Ultra", href: "/products/inext-tv6-ultra" },
        { labelKey: "footerLinkTv6", href: "/products/inext-tv6" },
        { labelKey: "footerLinkTv5Ultra", href: "/products/inext-tv5-ultra" },
        { labelKey: "footerLinkTv5", href: "/products/inext-tv5" },
        { labelKey: "footerLinkAccessories", href: "/products/accessories" },
        { labelKey: "footerLinkArchive", href: "/products/archive" },
      ],
    },
  ],
  [
    {
      headingKey: "footerSolutions",
      headingHref: "/solutions",
      links: [
        { labelKey: "footerLinkAlcatrazDms", href: "/solutions/alcatraz-dms" },
        { labelKey: "footerLinkOta", href: "/solutions/ota-updates" },
        { labelKey: "navSolutionsCustomization", href: "/solutions/customization" },
        { labelKey: "footerLinkIptvApp", href: "/solutions/iptv-app" },
        { labelKey: "footerLinkRemoteApp", href: "/solutions/inext-remote" },
        { labelKey: "footerLinkLogistics", href: "/solutions/logistics" },
      ],
    },
  ],
  [
    {
      headingKey: "footerGuides",
      headingHref: "/guides",
      links: [
        { labelKey: "footerLinkGuidesStbChecklist", href: "/guides/iptv-stb-buyers-checklist" },
        { labelKey: "footerLinkGuidesTco", href: "/tools/stb-tco-calculator" },
        {
          labelKey: "footerLinkGuidesRamStorage",
          href: "/guides/stb-ram-and-storage",
        },
        { labelKey: "footerLinkGuidesAndroidLinux", href: "/guides/android-vs-linux-stb" },
        { labelKey: "footerLinkGuidesAospCert", href: "/guides/aosp-vs-certified-android-tv" },
        { labelKey: "guidesIndexAllTitle", href: "/guides", all: true },
      ],
    },
  ],
  [
    {
      headingKey: "footerCompany",
      links: [
        { labelKey: "footerLinkAbout", href: "/about" },
        { labelKey: "footerLinkBrand", href: "/brand" },
        { labelKey: "footerLinkContact", href: "/contacts" },
        { labelKey: "footerLinkDistributors", href: "/distributors" },
        { labelKey: "footerLinkBlog", href: "/blog" },
        { labelKey: "footerLinkNewsletter", href: "/newsletter" },
        { labelKey: "footerLinkFaq", href: "/faq" },
      ],
    },
  ],
] as const;

const utilityLinks = [
  {
    labelKey: "footerLinkServiceStatus",
    href: "https://status.inextapi.com/",
    external: true,
  },
  { labelKey: "footerLinkPrivacy", href: "/privacy" },
  { labelKey: "footerLinkRepublishing", href: "/republishing" },
] as const;

export function SiteFooter() {
  const { messages, site, href } = useSite();
  const home = localizeUrl("/", site.locale, href);
  const footerHref = (destination: string) =>
    destination.startsWith("#")
      ? `${home}${destination}`
      : localizeUrl(destination, site.locale, href);

  return (
    <footer className="relative z-20 border-t border-border bg-background">
      <div className="mx-auto max-w-7xl px-6 py-16">
        <div className="grid grid-cols-2 gap-x-8 gap-y-10 md:grid-cols-3 xl:grid-cols-6">
          <div className="col-span-2 md:col-span-3 xl:col-span-2">
            <a href={localizeUrl("/", site.locale, href)} className="flex items-center gap-2">
              <img src={logo} alt="inext" className="h-7 w-auto" />
            </a>
            <p className="mt-4 max-w-sm text-sm text-muted-foreground">
              {messages.footerDescription}
            </p>
            <div className="mt-6 flex items-center gap-2">
              {[
                {
                  Icon: Linkedin,
                  href: "https://www.linkedin.com/company/inext-ltd-/",
                  label: "LinkedIn",
                },
                { Icon: Youtube, href: "https://www.youtube.com/user/inextltd", label: "YouTube" },
                { Icon: Twitter, href: "https://x.com/inext_eu", label: "X" },
                { Icon: Facebook, href: "https://www.facebook.com/inext.ua", label: "Facebook" },
              ].map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:text-foreground"
                  aria-label={label}
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {columnGroups.map((sections) => (
            <div key={sections[0].headingKey} className="min-w-0">
              {sections.map((section, index) => (
                <div key={section.headingKey} className={index === 0 ? undefined : "mt-6"}>
                  <h2 className="text-sm font-semibold text-foreground">
                    {"headingHref" in section ? (
                      <a
                        href={footerHref(section.headingHref)}
                        className="transition-colors hover:text-primary"
                      >
                        {messages[section.headingKey]}
                      </a>
                    ) : (
                      messages[section.headingKey]
                    )}
                  </h2>
                  <ul className="mt-2.5">
                    {section.links.map((link) => (
                      <li key={link.labelKey}>
                        <a
                          href={"external" in link ? link.href : footerHref(link.href)}
                          target={"external" in link ? "_blank" : undefined}
                          rel={"external" in link ? "noopener noreferrer" : undefined}
                          className={
                            "all" in link
                              ? "inline-flex items-center gap-1.5 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
                              : "block py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
                          }
                        >
                          {messages[link.labelKey]}
                          {"all" in link && <ArrowRight aria-hidden className="h-3.5 w-3.5" />}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col gap-4 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} inext. {messages.footerRights}
          </p>
          <ul className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground">
            {utilityLinks.map((link) => (
              <li key={link.labelKey}>
                <a
                  href={"external" in link ? link.href : footerHref(link.href)}
                  target={"external" in link ? "_blank" : undefined}
                  rel={"external" in link ? "noopener noreferrer" : undefined}
                  className="transition-colors hover:text-foreground"
                >
                  {messages[link.labelKey]}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}
